import type { Speed, Voice } from "./db";
import { synthesizeChunk, TtsError } from "./tts";

type ChunkStatus = "idle" | "loading" | "ready" | "error";

interface CacheEntry {
  status: ChunkStatus;
  url?: string;
  blob?: Blob;
  error?: string;
}

export interface EngineState {
  index: number;
  totalChunks: number;
  isPlaying: boolean;
  currentTime: number;
  duration: number | null;
  chunkStatus: ChunkStatus;
  chunkError?: string;
  elapsedSec: number;
  totalEstimatedSec: number;
  isFinished: boolean;
}

const FALLBACK_SEC_PER_CHAR = 1 / 13;
const RETRY_ATTEMPTS = 3;
const PREFETCH_AHEAD = 2;
const MAX_CACHE_ENTRIES = 8;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Owns a single <audio> element and the generated-audio cache for one
 * document. Chunks are generated on demand and prefetched ahead of
 * playback; failures retry in place and never reset document position.
 */
export class PlaybackEngine {
  private chunks: string[];
  private voice: Voice;
  private speed: Speed;
  private index: number;
  private audio: HTMLAudioElement;
  private cache = new Map<string, CacheEntry>();
  private cacheOrder: string[] = [];
  private inFlight = new Map<string, Promise<CacheEntry>>();
  private durations: Array<number | null>;
  private listeners = new Set<(state: EngineState) => void>();
  private isFinished = false;
  private destroyed = false;

  constructor(chunks: string[], voice: Voice, speed: Speed, startIndex = 0) {
    this.chunks = chunks;
    this.voice = voice;
    this.speed = speed;
    this.index = Math.min(Math.max(startIndex, 0), Math.max(chunks.length - 1, 0));
    this.durations = chunks.map(() => null);

    this.audio = new Audio();
    this.audio.preload = "auto";
    this.audio.playbackRate = speed;

    this.audio.addEventListener("timeupdate", this.emit);
    this.audio.addEventListener("play", this.emit);
    this.audio.addEventListener("pause", this.emit);
    this.audio.addEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.audio.addEventListener("ended", this.handleEnded);
  }

  private key(index: number): string {
    return `${this.voice}:${index}`;
  }

  private handleLoadedMetadata = () => {
    if (Number.isFinite(this.audio.duration)) {
      this.durations[this.index] = this.audio.duration;
    }
    this.emit();
  };

  private handleEnded = () => {
    if (this.index + 1 < this.chunks.length) {
      void this.loadIndex(this.index + 1, { autoplay: true });
    } else {
      this.isFinished = true;
      this.emit();
    }
  };

  private touchCache(k: string) {
    const pos = this.cacheOrder.indexOf(k);
    if (pos >= 0) this.cacheOrder.splice(pos, 1);
    this.cacheOrder.push(k);
    while (this.cacheOrder.length > MAX_CACHE_ENTRIES) {
      const evictKey = this.cacheOrder.shift();
      if (!evictKey) continue;
      const evictIndex = Number(evictKey.split(":").pop());
      if (evictIndex === this.index) continue;
      const entry = this.cache.get(evictKey);
      if (entry?.url) URL.revokeObjectURL(entry.url);
      this.cache.delete(evictKey);
    }
  }

  private async ensureChunkReady(index: number): Promise<CacheEntry> {
    const text = this.chunks[index];
    if (text === undefined) throw new Error(`No chunk at index ${index}`);
    const k = this.key(index);

    const existing = this.cache.get(k);
    if (existing?.status === "ready") {
      this.touchCache(k);
      return existing;
    }

    const pending = this.inFlight.get(k);
    if (pending) return pending;

    const task = (async (): Promise<CacheEntry> => {
      this.cache.set(k, { status: "loading" });
      if (index === this.index) this.emit();

      let lastError: unknown;
      for (let attempt = 0; attempt <= RETRY_ATTEMPTS; attempt++) {
        if (this.destroyed) throw new Error("Engine destroyed");
        try {
          const blob = await synthesizeChunk(text, this.voice);
          const url = URL.createObjectURL(blob);
          const entry: CacheEntry = { status: "ready", url, blob };
          this.cache.set(k, entry);
          this.touchCache(k);
          if (index === this.index) this.emit();
          return entry;
        } catch (err) {
          lastError = err;
          if (attempt < RETRY_ATTEMPTS) await delay(500 * (attempt + 1));
        }
      }

      const message = lastError instanceof TtsError ? lastError.message : "Failed to generate audio";
      const entry: CacheEntry = { status: "error", error: message };
      this.cache.set(k, entry);
      if (index === this.index) this.emit();
      throw lastError instanceof Error ? lastError : new Error(message);
    })();

    this.inFlight.set(k, task);
    try {
      return await task;
    } finally {
      this.inFlight.delete(k);
    }
  }

  private prefetchAhead() {
    for (let offset = 1; offset <= PREFETCH_AHEAD; offset++) {
      const i = this.index + offset;
      if (i >= this.chunks.length) continue;
      const k = this.key(i);
      const entry = this.cache.get(k);
      if (entry?.status === "ready" || entry?.status === "loading" || this.inFlight.has(k)) continue;
      void this.ensureChunkReady(i).catch(() => {
        /* surfaced via cache status; prefetch failures don't block playback */
      });
    }
  }

  private async loadIndex(index: number, opts: { autoplay: boolean; atTime?: number }): Promise<void> {
    if (index < 0 || index >= this.chunks.length) return;
    this.index = index;
    this.isFinished = false;
    this.emit();

    try {
      const entry = await this.ensureChunkReady(index);
      if (this.destroyed || this.index !== index) return;
      if (!entry.url) return;

      this.audio.src = entry.url;
      this.audio.playbackRate = this.speed;

      await new Promise<void>((resolve) => {
        const onReady = () => {
          this.audio.removeEventListener("loadedmetadata", onReady);
          resolve();
        };
        if (this.audio.readyState >= 1) resolve();
        else this.audio.addEventListener("loadedmetadata", onReady, { once: true });
      });

      if (this.destroyed || this.index !== index) return;
      this.audio.currentTime = Math.max(0, opts.atTime ?? 0);
      if (opts.autoplay) {
        await this.audio.play().catch(() => undefined);
      }
      this.prefetchAhead();
    } catch {
      // Error state already recorded in cache/emit; position is preserved.
    }
  }

  private estimateDuration(index: number): number {
    const known = this.durations[index];
    if (known != null) return known;
    const chunk = this.chunks[index] ?? "";
    const knownPairs = this.durations
      .map((d, i) => (d != null ? d / Math.max(this.chunks[i]?.length ?? 1, 1) : null))
      .filter((v): v is number => v != null);
    const rate =
      knownPairs.length > 0
        ? knownPairs.reduce((a, b) => a + b, 0) / knownPairs.length
        : FALLBACK_SEC_PER_CHAR;
    return chunk.length * rate;
  }

  getState = (): EngineState => {
    const k = this.key(this.index);
    const entry = this.cache.get(k);
    const chunkStatus: ChunkStatus = entry?.status ?? "idle";

    let elapsedBefore = 0;
    for (let i = 0; i < this.index; i++) elapsedBefore += this.durations[i] ?? this.estimateDuration(i);

    let totalEstimatedSec = 0;
    for (let i = 0; i < this.chunks.length; i++) totalEstimatedSec += this.durations[i] ?? this.estimateDuration(i);

    return {
      index: this.index,
      totalChunks: this.chunks.length,
      isPlaying: !this.audio.paused && !this.audio.ended,
      currentTime: this.audio.currentTime || 0,
      duration: Number.isFinite(this.audio.duration) ? this.audio.duration : this.durations[this.index] ?? null,
      chunkStatus,
      chunkError: entry?.status === "error" ? entry.error : undefined,
      elapsedSec: elapsedBefore + (this.audio.currentTime || 0),
      totalEstimatedSec,
      isFinished: this.isFinished,
    };
  };

  private emit = () => {
    if (this.destroyed) return;
    const state = this.getState();
    for (const listener of this.listeners) listener(state);
  };

  subscribe(listener: (state: EngineState) => void): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  async play(): Promise<void> {
    if (this.audio.src) {
      this.isFinished = false;
      await this.audio.play().catch(() => undefined);
      this.prefetchAhead();
    } else {
      await this.loadIndex(this.index, { autoplay: true });
    }
  }

  pause(): void {
    this.audio.pause();
  }

  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.emit();
  }

  async next(): Promise<void> {
    const wasPlaying = !this.audio.paused;
    await this.loadIndex(this.index + 1, { autoplay: wasPlaying });
  }

  async prev(): Promise<void> {
    const wasPlaying = !this.audio.paused;
    await this.loadIndex(this.index - 1, { autoplay: wasPlaying });
  }

  async retryCurrent(): Promise<void> {
    const k = this.key(this.index);
    this.cache.delete(k);
    const wasPlaying = !this.audio.paused;
    await this.loadIndex(this.index, { autoplay: wasPlaying });
  }

  /** Rewind/forward across chunk boundaries using known or estimated durations. */
  async seekRelative(deltaSec: number): Promise<void> {
    const wasPlaying = !this.audio.paused;
    const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : this.estimateDuration(this.index);
    let target = (this.audio.currentTime || 0) + deltaSec;

    if (target < 0) {
      if (this.index === 0) {
        this.audio.currentTime = 0;
        return;
      }
      let remainder = -target;
      let i = this.index - 1;
      while (i > 0 && remainder > this.estimateDuration(i)) {
        remainder -= this.estimateDuration(i);
        i--;
      }
      const atTime = Math.max(0, this.estimateDuration(i) - remainder);
      await this.loadIndex(i, { autoplay: wasPlaying, atTime });
      return;
    }

    if (target > duration) {
      if (this.index + 1 >= this.chunks.length) {
        this.audio.currentTime = duration;
        return;
      }
      let remainder = target - duration;
      let i = this.index + 1;
      while (i < this.chunks.length - 1 && remainder > this.estimateDuration(i)) {
        remainder -= this.estimateDuration(i);
        i++;
      }
      await this.loadIndex(i, { autoplay: wasPlaying, atTime: Math.max(0, remainder) });
      return;
    }

    this.audio.currentTime = target;
  }

  /** Seeks to an absolute position along the whole-document estimated timeline. */
  async seekToGlobal(targetSec: number): Promise<void> {
    const wasPlaying = !this.audio.paused;
    let remaining = Math.max(0, targetSec);
    let i = 0;
    while (i < this.chunks.length - 1) {
      const dur = this.durations[i] ?? this.estimateDuration(i);
      if (remaining <= dur) break;
      remaining -= dur;
      i++;
    }
    await this.loadIndex(i, { autoplay: wasPlaying, atTime: remaining });
  }

  /**
   * Generates every chunk (reusing already-cached audio) and concatenates
   * them into a single downloadable MP3 blob, in document order.
   */
  async exportAsMp3(onProgress?: (done: number, total: number) => void): Promise<Blob> {
    const parts: Blob[] = [];
    for (let i = 0; i < this.chunks.length; i++) {
      const entry = await this.ensureChunkReady(i);
      if (entry.blob) parts.push(entry.blob);
      onProgress?.(i + 1, this.chunks.length);
    }
    return new Blob(parts, { type: "audio/mpeg" });
  }

  setSpeed(speed: Speed): void {
    this.speed = speed;
    this.audio.playbackRate = speed;
    this.emit();
  }

  async setVoice(voice: Voice): Promise<void> {
    if (voice === this.voice) return;
    this.voice = voice;
    this.durations = this.chunks.map(() => null);
    const wasPlaying = !this.audio.paused;
    await this.loadIndex(this.index, { autoplay: wasPlaying, atTime: 0 });
  }

  getVoice(): Voice {
    return this.voice;
  }

  getSpeed(): Speed {
    return this.speed;
  }

  getIndex(): number {
    return this.index;
  }

  getCurrentTime(): number {
    return this.audio.currentTime || 0;
  }

  destroy(): void {
    this.destroyed = true;
    this.audio.pause();
    this.audio.removeEventListener("timeupdate", this.emit);
    this.audio.removeEventListener("play", this.emit);
    this.audio.removeEventListener("pause", this.emit);
    this.audio.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.audio.removeEventListener("ended", this.handleEnded);
    this.audio.src = "";
    for (const entry of this.cache.values()) {
      if (entry.url) URL.revokeObjectURL(entry.url);
    }
    this.cache.clear();
    this.listeners.clear();
  }
}
